import React, { useEffect, useState } from 'react';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableModelFilterApplied,
  fetchAllModels,
  fetchModelFilters,
  setModelLimit,
  setModelPage,
  setModelSortBy,
  setModelSortField,
  updateModelFilters,
} from '../../../redux/slices/ModelSlice';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import deviceApi from '../../../utils/api/device-hub';
import AddModelDrawer from '../../ev-device-hub/components/drawers/AddModelDrawer';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Local models list component — displays models belonging to the current tenant.
 * @returns {React.JSX.Element} Local models table
 */
function Local() {
  const dispatch = useAppDispatch();
  const {
    models,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.model);

  const [modelDrawerOpen, setModelDrawerOpen] = useState(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: null,
    header: false,
    content: null,
    notificationModal: false,
  });

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllModels({
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllModels({
          page: DefaultApiLimit.page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableModelFilterApplied());
    }
  }, [isFilterUpdated]);

  /**
   * Handles sort change from CustomTable
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setModelSortBy(sortData.sortBy));
    dispatch(setModelSortField(sortData.sortField));
    dispatch(
      fetchAllModels({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and limit changes
   * @param {any} value - Page and limit data
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setModelPage(value.page));
    if (value.limit !== undefined) dispatch(setModelLimit(value.limit));
    dispatch(
      fetchAllModels({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes model data using current filters
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllModels({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Calls the API to delete a model
   * @param {string} modelId - The ID of the model to delete
   */
  const handleConfirmDeleteModel = (modelId: string) => {
    deviceApi
      .deleteModel(modelId)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title="Model deleted successfully"
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                handleRefresh();
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong',
            },
          }),
        );
      });
  };

  /**
   * Triggers the delete confirmation warning modal for a model
   * @param {string} modelId - The ID of the model
   */
  const handleDeleteModelClick = (modelId: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={modelId}
          title="Are you sure you want to delete this model?"
          description="This action cannot be undone."
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(id) => id && handleConfirmDeleteModel(id)}
        />
      ),
      notificationModal: true,
    });
  };

  const column = [
    {
      label: 'Model Name',
      accessorKey: 'name',
    },
    {
      label: 'Brand Name',
      accessorKey: 'brandId',
      /**
       * Renders brand name
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span>
          {currentRow.brand?.name ||
            currentRow.brandId?.name ||
            currentRow.brandName ||
            currentRow.brandId ||
            '-'}
        </span>
      ),
    },
    {
      label: 'Media',
      accessorKey: 'imageUrls',
      /**
       * Renders model media preview
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => {
        const image =
          currentRow.imageUrl ||
          currentRow.imageUrls?.[0]?.signedUrl ||
          currentRow.imageUrls?.[0]?.url;
        return image ? (
          <div className="model-media-cell">
            <div className="model-media-wrapper">
              <img src={image} alt={currentRow.name || 'Model'} className="model-media-thumbnail" />
            </div>
          </div>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      label: 'Device Count',
      accessorKey: 'deviceCount',
      sortAllow: true,
      /**
       * Renders device count
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{currentRow.deviceCount || 0}</span>,
    },
    {
      label: 'Actions',
      accessorKey: 'actions',
      sortAllow: false,
      columnWidth: 80,
      sticky: 'right' as const,
      /**
       * Renders row action buttons
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <div className="action-buttons-wrap">
          <div
            role="presentation"
            className="action-button"
            onClick={(event) => {
              event.stopPropagation();
              handleDeleteModelClick(currentRow._id);
            }}>
            <DeleteIcon className="icon delete" />
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />

      <CustomTable
        dataKey="ModelManagement"
        header={column}
        data={models}
        serialNo
        version="v2"
        loading={loading}
        page={page}
        limit={limit}
        height={350}
        created
        lastUpdated
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={handlePageLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateModelFilters}
        filterListLoading={isFilterUpdated}
        filterListToDispatch={fetchModelFilters}
        actionButton
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel="Refresh"
        refershCurrentPages={handleRefresh}
        onHiddenColumnsChange={() => {}}
        rightTopContent={
          <div className="table-bottom-button-list">
            {permissionCheck('devicesModelsAdd') && (
              <div className="table-button-item">
                <MyButton
                  label="Add Model"
                  buttonType="submit"
                  onClickFunc={() => setModelDrawerOpen(true)}
                />
              </div>
            )}
          </div>
        }
      />

      <AddModelDrawer
        open={modelDrawerOpen}
        onClose={() => setModelDrawerOpen(false)}
        onModelCreated={() => {
          setModelDrawerOpen(false);
          handleRefresh();
        }}
      />
    </>
  );
}

export default Local;
